/*
  ONJ CUSTOM — onj-mega-menu-cards.js
  Purpose:  Copies the desktop mega menu card sets into the matching mobile
            drawer submenu, so one set of Customizer blocks drives both.
            Also points each drawer submenu's "Shop all" at that dropdown's
            top-level link, or hides it when that link is blank or "#"
            (desktop does this in Liquid, mega-menu-list.liquid).
  Consumed: snippets/onj-mega-menu-cards.liquid (script tag);
            reads the [data-menu-cards-json] script emitted by blocks/_header-menu.liquid;
            appends a [data-onj-drawer-cards] slot to each drawer submenu
            (.menu-drawer__submenu#link-<handle>) from snippets/header-drawer.liquid —
            injected here so no edit to that base file is needed
  Added:    2026-10 · Horizon migration R1
  Round 2:  Retire if Horizon's drawer renders custom menu cards per submenu natively
*/
(() => {
  const SUBMENU = '.menu-drawer__submenu[id^="link-"] > .menu-drawer__inner-submenu';
  const TARGET = '[data-onj-drawer-cards]:not([data-onj-filled])';

  // Drawer submenu ids are `link-{{ link.handle }}`; a link's handle is its
  // handleized title, which is the same key the desktop card sets use.
  function addSlots() {
    document.querySelectorAll(SUBMENU).forEach((inner) => {
      if (inner.querySelector(':scope > [data-onj-drawer-cards]')) return;
      const key = inner.parentElement?.id.slice('link-'.length);
      if (!key) return;
      const slot = document.createElement('div');
      slot.className = 'menu-drawer__featured-content menu-drawer__featured-content--childlist onj-drawer-cards';
      slot.dataset.onjDrawerCards = key;
      inner.append(slot);
    });
  }
  /** @type {Document | null} */
  let source = null;
  let sourceText = '';

  function getSource() {
    const script = document.querySelector('[data-menu-cards-json]');
    if (!script) return null;
    // Re-parse only if the header was re-rendered with different cards.
    if (source && script.textContent === sourceText) return source;
    try {
      sourceText = script.textContent;
      source = new DOMParser().parseFromString(JSON.parse(sourceText), 'text/html');
    } catch (error) {
      console.warn('[onj-mega-menu-cards] could not parse menu cards JSON', error);
      source = null;
    }
    return source;
  }

  function fill() {
    const doc = getSource();
    if (!doc) return;
    addSlots();
    const targets = document.querySelectorAll(TARGET);
    if (!targets.length) return;

    targets.forEach((target) => {
      if (!(target instanceof HTMLElement)) return;
      target.dataset.onjFilled = '';
      const key = target.dataset.onjDrawerCards;
      if (!key) return;
      const set = doc.querySelector(`[data-menu-cards="${CSS.escape(key)}"]`);
      if (!set) return;

      const list = /** @type {HTMLElement} */ (set.cloneNode(true));
      list.removeAttribute('data-menu-cards');
      list.className = 'menu-drawer__featured-content-list list-unstyled';
      target.append(list);
    });
  }

  // Drawer "Shop all" is hard-coded to all products in header-drawer.liquid.
  // Match each drawer submenu to its desktop top-level link by title and reuse
  // that href; hide the button when that link is blank or "#". Submenus with
  // no desktop match are left untouched.
  function syncShopAll() {
    const hrefByTitle = new Map();
    document.querySelectorAll('.menu-list__list-item > a.menu-list__link').forEach((link) => {
      const title = link.querySelector('.menu-list__link-title')?.textContent?.trim();
      if (title) hrefByTitle.set(title, link.getAttribute('href') || '');
    });
    if (!hrefByTitle.size) return;

    document.querySelectorAll('.menu-drawer__submenu[id^="link-"]').forEach((submenu) => {
      const shopAll = submenu.querySelector('a.menu-drawer__shop-all:not([data-onj-shop-all])');
      if (!(shopAll instanceof HTMLAnchorElement)) return;
      shopAll.dataset.onjShopAll = '';
      const title = submenu.parentElement
        ?.querySelector(':scope > summary .menu-drawer__menu-item-text')
        ?.textContent?.trim();
      if (!title || !hrefByTitle.has(title)) return;
      const href = hrefByTitle.get(title);
      if (href && href !== '#') {
        shopAll.setAttribute('href', href);
      } else {
        shopAll.hidden = true;
        shopAll.style.display = 'none';
      }
    });
  }

  function run() {
    syncShopAll();
    fill();
  }

  let queued = false;
  function queueFill() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      run();
    });
  }

  function init() {
    run();
    // Header hydration / section re-renders replace drawer markup; refill when that happens.
    const root = document.querySelector('header-component') || document.body;
    new MutationObserver(queueFill).observe(root, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
